import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, ArrowRightLeft, History, LogOut, Wallet } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import { CURRENCY_SYMBOLS } from "@/lib/constants";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [balance, setBalance] = useState("");
  const [currency, setCurrency] = useState("XPF");
  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    const storedBalance = sessionStorage.getItem("userBalance");
    if (!storedBalance) {
      navigate("/");
      return;
    }
    setBalance(storedBalance);
    setCurrency(sessionStorage.getItem("userCurrency") || "XPF");
    
    const now = new Date();
    const locales = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', pt: 'pt-BR', it: 'it-IT' };
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    setCurrentDate(now.toLocaleDateString(locales[language] || 'fr-FR', options));
  }, [navigate, language]);

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const handleTransfer = () => {
    navigate("/withdrawal");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-fmi.png" alt="FMI" className="w-10 h-10 rounded-xl" />
            <span className="font-semibold text-[#0F172A]">FMI Pay</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#64748B] hover:text-[#0F172A]"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t("logout")}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 pb-32">
        {/* Welcome Message */}
        <div className="mb-6 animate-fade-in">
          <p className="text-[#64748B] text-sm">{currentDate}</p>
          <h1 className="text-2xl font-bold text-[#0F172A] mt-1">
            {t("welcomeAccount")}
          </h1>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white border-0 shadow-xl mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }} data-testid="balance-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-5 h-5 text-[#2563EB]" />
              <span className="text-sm text-white/70 uppercase tracking-wider font-medium">
                {t("availableBalance")}
              </span>
            </div>
            <div className="balance-display text-white text-2xl" data-testid="balance-amount">
              {balance} <span className="text-lg font-semibold text-white/70">{CURRENCY_SYMBOLS[currency] || currency}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs text-white/50">
                {t("lastUpdate")}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <Card 
            className="form-container card-hover cursor-pointer border-2 border-transparent hover:border-[#2563EB]"
            onClick={handleTransfer}
            data-testid="transfer-card"
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mb-4">
                <ArrowRightLeft className="w-6 h-6 text-[#2563EB]" />
              </div>
              <h3 className="font-semibold text-[#0F172A] mb-1">{t("makeWithdrawal")}</h3>
              <p className="text-sm text-[#64748B]">
                {t("transferFunds")}
              </p>
            </CardContent>
          </Card>

          <Card className="form-container card-hover cursor-pointer opacity-50" data-testid="history-card">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-[#64748B]/10 rounded-xl flex items-center justify-center mb-4">
                <History className="w-6 h-6 text-[#64748B]" />
              </div>
              <h3 className="font-semibold text-[#0F172A] mb-1">{t("history")}</h3>
              <p className="text-sm text-[#64748B]">
                {t("viewTransactions")}
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Security Footer */}
      <div className="mt-8 py-4 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="security-badge">
            <ShieldCheck className="w-4 h-4" />
            <span>{t("secureSpace")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
